import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
} from '@mui/joy';
import {
  XCircle as ErrorIcon,
  ArrowsClockwise as Refresh,
  House as Home,
} from '@phosphor-icons/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch and display errors gracefully
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Check if this is a PDF worker error
    const isPdfWorkerError = 
      error.message?.includes('sendWithPromise') ||
      error.message?.includes('worker') ||
      error.message?.includes('Cannot read properties of null');

    if (isPdfWorkerError) {
      console.warn('PDF worker error detected in ErrorBoundary, attempting to re-initialize...');
      // Dynamically import and re-initialize the worker
      import('../../lib/pdfWorkerSetup').then(({ reinitializeWorker }) => {
        reinitializeWorker();
        // Reset error state after a short delay to allow retry
        setTimeout(() => {
          this.handleReset();
        }, 1000);
      }).catch((err) => {
        console.error('Failed to re-initialize PDF worker:', err);
      });
    }

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Send error to monitoring service (e.g., Sentry)
    // This is where you'd integrate with your error monitoring service
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/accounting';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            p: 3,
          }}
        >
          <Card variant="outlined" sx={{ maxWidth: 600, width: '100%' }}>
            <CardContent>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <ErrorIcon
                  size={64}
                  color="var(--joy-palette-danger-500)"
                  style={{ marginBottom: 16 }}
                />
                <Typography level="h3" sx={{ mb: 1 }}>
                  Oops! Something went wrong
                </Typography>
                <Typography level="body-md" sx={{ color: 'text.secondary' }}>
                  We encountered an unexpected error. The issue has been logged
                  and we'll look into it.
                </Typography>
              </Box>

              {/* Show error details in development */}
              {import.meta.env.DEV && this.state.error && (
                <Alert
                  color="danger"
                  variant="soft"
                  sx={{ mb: 3, textAlign: 'left' }}
                >
                  <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                    {this.state.error.toString()}
                  </Typography>
                  {this.state.errorInfo && (
                    <details style={{ marginTop: '8px' }}>
                      <summary style={{ cursor: 'pointer' }}>
                        Component Stack
                      </summary>
                      <pre
                        style={{
                          fontSize: '12px',
                          overflow: 'auto',
                          marginTop: '8px',
                        }}
                      >
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="solid"
                  color="primary"
                  startDecorator={<Refresh />}
                  onClick={this.handleReset}
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startDecorator={<Home />}
                  onClick={this.handleGoHome}
                >
                  Go to Dashboard
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * Async Error Boundary for handling promise rejections
 */
export const AsyncErrorBoundary: React.FC<{
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ children, fallback }) => {
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      setError(new Error(event.reason));
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  if (error) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <Alert color="danger" variant="soft">
        <Typography level="body-sm">
          An error occurred while loading data: {error.message}
        </Typography>
        <Button
          size="sm"
          variant="plain"
          color="danger"
          onClick={() => setError(null)}
          sx={{ mt: 1 }}
        >
          Dismiss
        </Button>
      </Alert>
    );
  }

  return <>{children}</>;
};

/**
 * Fallback component for specific error types
 */
export const ErrorFallback: React.FC<{
  error?: Error;
  resetError?: () => void;
  message?: string;
}> = ({ error, resetError, message }) => (
  <Box sx={{ p: 3, textAlign: 'center' }}>
    <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
      <Typography level="body-md">
        {message || 'Unable to load this content'}
      </Typography>
      {error && import.meta.env.DEV && (
        <Typography level="body-sm" sx={{ mt: 1, fontFamily: 'monospace' }}>
          {error.message}
        </Typography>
      )}
    </Alert>
    {resetError && (
      <Button
        variant="outlined"
        color="primary"
        size="sm"
        onClick={resetError}
        startDecorator={<Refresh />}
      >
        Try Again
      </Button>
    )}
  </Box>
);

// Type declaration for Sentry (if using)
declare global {
  interface Window {
    Sentry?: {
      captureException: (error: Error, context?: Record<string, unknown>) => void;
    };
  }
}